import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

import { ApiError } from '@/shared/api/client';
import {
  brokers,
  emptyAddress,
  type Address,
  type BrokerAddress,
} from '@/shared/api/resources';
import { AddressFields } from '@/shared/ui/AddressFields';
import { Alert } from '@/shared/ui/Alert';
import { Badge } from '@/shared/ui/Badge';
import { Button } from '@/shared/ui/Button';
import { DataTable } from '@/shared/ui/DataTable';
import { DetailCard } from '@/shared/ui/DetailLayout';
import { EmptyState } from '@/shared/ui/EmptyState';
import { ConfirmDialog, Modal } from '@/shared/ui/Modal';
import { useToast } from '@/shared/ui/useToast';

function linhaDoEndereco(endereco: BrokerAddress): string {
  const rua = [endereco.street, endereco.number].filter(Boolean).join(', ');
  const cidade = [endereco.city, endereco.state].filter(Boolean).join('/');
  return [rua, endereco.complement, endereco.district, cidade]
    .filter(Boolean)
    .join(' - ');
}

/**
 * Enderecos da corretora (Locations do OnPoint), com o principal espelhado no
 * cadastro: marcar outro como principal muda o endereco do cadastro, e a edicao do
 * endereco no cadastro muda o principal. O principal so sai quando e o ultimo.
 */
export function BrokerAddressesCard({
  corretoraId,
  podeEditar,
  onMudou,
}: {
  corretoraId: string;
  podeEditar: boolean;
  onMudou: () => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [incluindo, setIncluindo] = useState(false);
  const [removendo, setRemovendo] = useState<BrokerAddress | null>(null);

  const consulta = useQuery({
    queryKey: ['broker', corretoraId, 'addresses'],
    queryFn: ({ signal }) => brokers.addresses(corretoraId, signal),
  });

  function invalidar() {
    void queryClient.invalidateQueries({ queryKey: ['broker', corretoraId] });
    void queryClient.invalidateQueries({ queryKey: ['brokers'] });
    onMudou();
  }

  const principal = useMutation({
    mutationFn: (id: string) =>
      brokers.updateAddress(corretoraId, id, { is_main: true }),
    onSuccess: () => {
      toast.success(
        'Endereco principal alterado. O cadastro passa a mostrar este endereco.',
      );
      invalidar();
    },
    onError: (erro: Error) => toast.error(erro.message),
  });

  const remover = useMutation({
    mutationFn: (id: string) => brokers.removeAddress(corretoraId, id),
    onSuccess: () => {
      toast.success('Endereco excluido.');
      setRemovendo(null);
      invalidar();
    },
    onError: (erro: Error) => toast.error(erro.message),
  });

  return (
    <DetailCard
      title="Enderecos"
      description="O principal e o endereco do cadastro."
      loading={consulta.isPending}
      error={consulta.error}
      action={
        podeEditar ? (
          <Button size="sm" variant="secondary" onClick={() => setIncluindo(true)}>
            Adicionar endereco
          </Button>
        ) : undefined
      }
    >
      <DataTable
        columns={[
          {
            key: 'endereco',
            header: 'Endereco',
            render: (endereco: BrokerAddress) => linhaDoEndereco(endereco),
          },
          {
            key: 'cep',
            header: 'CEP',
            hideBelow: 'md',
            render: (endereco: BrokerAddress) => endereco.zip_code,
          },
          {
            key: 'situacao',
            header: 'Situacao',
            render: (endereco: BrokerAddress) => (
              <div className="flex gap-1">
                {endereco.is_main ? <Badge tone="brand">Principal</Badge> : null}
                {endereco.active ? null : <Badge tone="neutral">Inativo</Badge>}
              </div>
            ),
          },
          ...(podeEditar
            ? [
                {
                  key: 'acoes',
                  header: 'Acoes',
                  render: (endereco: BrokerAddress) => (
                    <div className="flex gap-1">
                      {endereco.is_main ? null : (
                        <Button
                          size="sm"
                          variant="ghost"
                          loading={principal.isPending}
                          onClick={() => principal.mutate(endereco.id)}
                        >
                          Tornar principal
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setRemovendo(endereco)}
                      >
                        Excluir
                      </Button>
                    </div>
                  ),
                },
              ]
            : []),
        ]}
        items={consulta.data ?? []}
        keyOf={(endereco) => endereco.id}
        empty={<EmptyState title="Nenhum endereco cadastrado" />}
      />

      {incluindo ? (
        <AddressDialog
          corretoraId={corretoraId}
          onClose={() => setIncluindo(false)}
          onSalvo={() => {
            setIncluindo(false);
            invalidar();
          }}
        />
      ) : null}

      <ConfirmDialog
        open={removendo !== null}
        onClose={() => setRemovendo(null)}
        onConfirm={() => removendo && remover.mutate(removendo.id)}
        title="Excluir o endereco?"
        message="O endereco principal so sai quando e o ultimo. Para trocar o principal, marque outro antes."
        confirmLabel="Excluir"
        tone="danger"
        loading={remover.isPending}
      />
    </DetailCard>
  );
}

function AddressDialog({
  corretoraId,
  onClose,
  onSalvo,
}: {
  corretoraId: string;
  onClose: () => void;
  onSalvo: () => void;
}) {
  const toast = useToast();
  const [endereco, setEndereco] = useState<Address>(emptyAddress);
  const [principal, setPrincipal] = useState(false);

  const salvar = useMutation({
    mutationFn: () =>
      brokers.addAddress(corretoraId, {
        zip_code: endereco.zip_code,
        street: endereco.street,
        number: endereco.number,
        complement: endereco.complement ?? '',
        district: endereco.district,
        city: endereco.city,
        state: endereco.state,
        is_main: principal,
      }),
    onSuccess: () => {
      toast.success('Endereco incluido.');
      onSalvo();
    },
  });
  const erro = salvar.error instanceof ApiError ? salvar.error : null;

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    salvar.mutate();
  }

  return (
    <Modal open onClose={onClose} title="Adicionar endereco" size="lg">
      <form onSubmit={enviar} className="flex flex-col gap-4">
        {erro ? (
          <Alert tone="danger" title="Nao foi possivel salvar">
            {erro.message}
          </Alert>
        ) : null}
        <AddressFields value={endereco} onChange={setEndereco} />
        <label className="flex items-center gap-2 text-xs text-ink-700">
          <input
            type="checkbox"
            checked={principal}
            onChange={(evento) => setPrincipal(evento.target.checked)}
          />
          Tornar este o endereco principal
        </label>
        <div className="flex justify-end gap-2 border-t border-line pt-4">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" loading={salvar.isPending}>
            Salvar
          </Button>
        </div>
      </form>
    </Modal>
  );
}
